<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { onDestroy, onMount } from "svelte";
import { fly } from "svelte/transition";
import { renderMarkdown } from "../../utils/markdownParser";

import FeedbackButtons from "../../components/FeedbackButtons.svelte";

import DateFilter from "../../components/DateFilter.svelte";
import DragAndDrop from "../../components/DragAndDrop.svelte";
import ExamplePrompts from "../../components/ExamplePrompts.svelte";
import FileUploadButton from "../../components/FileUploadButton.svelte";
import InputTextarea from "../../components/InputTextarea.svelte";
import ModelSelection from "../../components/ModelSelection.svelte";
import SkeletonLoader from "../../components/SkeletonLoader.svelte";
import Warning from "../../components/Warning.svelte";
import sendFeedback from "../../utils/SendFeedback.js";
import uiStore from "../../utils/UIStore";
import { formatDate } from "../../utils/dateUtils";
import {
	getAktuelleLegislatur,
	getDateRangeString,
} from "../../utils/dateUtils.js";
import websocketsStore from "../../utils/websocketsStore";
import Accordeon from "./Accordeon.svelte";

/* Variable set from outside component*/
export let onlyDocuments = false;

/* Variables and Constants */
let sources = []; // list of sources { type: "file|database|database_all_sources", origin: "clipboard | upload", label:"Angezeigter Text...", content: "", meta: {date_range: []} }
let _currentlySelectedDBSources = [];
let answers = []; // list of question/answer-pairs {type: "file|database|database_all_sources", state: "pending|done|error", timestamp: timestamp, answers: [{question: "question", answer: "answer", "model": generativeLLM, meta: { sources: [{ content: "", meta: { file_name: "", page_number: ""} }] }, feedback: {rating: 5, text: "feedback text"} }]}
let currentQuestion = "";
let llm = {};
let llm_selected_by_user = false;
let available_llms = [];
let isTextFieldActive = false;
let showDBSourceSelect = false; //show/hide Toggle on click
let isDropzoneActive = false;
let status = true;
let sendButtonTooltip = "";
let availableSources = [];
let rechercheNotAvailableText = "";
let askTheDocNotAvailableText = "";
let rechercheText = "";
let askTheDocText = "";
let warningCloud = "";
let warningRecherche = "";
let resetTextArea;
let examplePrompts = [];
let modelSelectionRef;

//---Svelte Component Lifecycle
onMount(() => {
	document.addEventListener("keydown", handleKeydown);
	updateSendButtonTooltip();
});

onDestroy(() => {
	document.removeEventListener("keydown", handleKeydown);
	if (!onlyDocuments) {
		uiStore.rechercheStore.update(() => ({
			sources: sources,
			_currentlySelectedDBSources: _currentlySelectedDBSources,
			answers: answers,
			currentQuestion: currentQuestion,
			llm: llm,
			llm_selected_by_user: llm_selected_by_user,
		}));
	} else {
		uiStore.documentStore.update(() => ({
			sources: sources,
			_currentlySelectedDBSources: _currentlySelectedDBSources,
			answers: answers,
			currentQuestion: currentQuestion,
			llm: llm,
			llm_selected_by_user: llm_selected_by_user,
		}));
	}
});

//---Interaction with UIStore
// load state from uistore
if (!onlyDocuments) {
	uiStore.rechercheStore.subscribe((store) => {
		sources = store.sources;
		_currentlySelectedDBSources = store._currentlySelectedDBSources;
		answers = store.answers;
		currentQuestion = store.currentQuestion;
		llm = store.llm;
		llm_selected_by_user = store.llm_selected_by_user;
	});
} else {
	uiStore.documentStore.subscribe((store) => {
		sources = store.sources;
		_currentlySelectedDBSources = store._currentlySelectedDBSources;
		answers = store.answers;
		currentQuestion = store.currentQuestion;
		llm = store.llm;
		llm_selected_by_user = store.llm_selected_by_user;
	});
}

uiStore.availableSourcesStore.subscribe((store) => {
	availableSources = store.available_sources;
});

//load available llms from uistore, updated every 30 seconds
uiStore.active_llms_recherche.subscribe((active_llms) => {
	available_llms = active_llms.recherche;
	const local_model = active_llms.recherche.find(
		(item) => item.name === "local",
	);
	//wenn vom user kein LLM ausgewählt wurde soll nach möglichkeit das lokale LLM gesetzt werden
	if (!llm_selected_by_user) {
		if (local_model) {
			llm = local_model;
		} else {
			llm = available_llms[0];
		}
	}
});

//load ui texts from ui Store
uiStore.textStore.subscribe((store) => {
	if (store.texts != null) {
		let data = store.texts;
		rechercheNotAvailableText = data.rechercheNotAvailable;
		askTheDocNotAvailableText = data.askTheDocNotAvailable;
		rechercheText = renderMarkdown(data.rechercheText);
		askTheDocText = renderMarkdown(data.askTheDocText);
		warningRecherche = renderMarkdown(data.warnhinweisRecherche);
		warningCloud = data.warnhinweisCloud;
	}
});

let uiTexts;
uiStore.textStore.subscribe((store) => {
	uiTexts = store.texts;
});

$: examplePrompts = uiTexts ? uiTexts.examplePromptsRecherche : [];

//---Copy from clipboard functions
function handleKeydown(event) {
	if (event.ctrlKey && event.key === "v" && !isTextFieldActive) {
		addTextFromClipboard();
	}
}

function addTextFromClipboard() {
	/*
        Paste text from clipboard to RAG-sources
         */
	navigator.clipboard
		.readText()
		.then((text) => {
			if (text.length === 0) {
				websocketsStore.currentMessage.info.update(() => ({
					type: "INFO",
					message: uiTexts.rag.clipboardEmpty,
				}));
				throw Error(uiTexts.rag.clipboardEmpty);
			}
			return text;
		})
		.then((text) => ({
			text: text,
			blob: new Blob([text], { type: "text/plain" }),
			label: text.substring(0, 25).trim(),
			filename: `Zwischenablage_${text
				.trim()
				.substring(0, 50)
				.split(" ")
				.slice(0, 6)
				.join(" ")
				.trim()
				.replace(/[\\\/:*?!"'`´<>|,;#\n\r\0]/g, "_")
				.trim()}.txt`,
		}))
		.then((data) => ({
			...data,
			file: new File([data.blob], data.filename, {
				type: "text/plain",
			}),
		}))
		.then((data) => {
			sources = [
				...sources,
				{
					id: window.crypto.randomUUID(),
					type: "file",
					origin: "clipboard",
					label: data.label,
					content: data.file,
					text: data.text,
				},
			];
			updateSendButtonTooltip();
		})
		.catch((reason) => console.debug(`${uiTexts.rag.genericError}·${reason}`));
}

//---File Upload
function addFiles(fileList) {
	/*
        Takes a FileList and adds the files to FormData to be sent to server
         */

	let _files = [];
	for (let i = 0; i < fileList.length; i++) {
		_files.push({
			id: window.crypto.randomUUID(),
			type: "file",
			origin: "upload",
			label: fileList[i].name,
			content: fileList[i],
		});
	}
	sources = [...sources, ..._files];
	updateSendButtonTooltip();
}

//---Selection of Database sources
function addDatabaseSources() {
	/*
        Hinzufügen einer einzelnen DB
        */

	// if database_all_sources is currently selected, then remove that item
	sources = sources.filter((item) => item.type !== "database_all_sources");

	//if sources were selected from dropdown and not from exampleprompts we have to enrich the data
	_currentlySelectedDBSources = _currentlySelectedDBSources.map((item) => {
		if (item.source === undefined) {
			return {
				source: item,
				startDate: "",
				endDate: "",
			};
		}
		return item;
	});

	const _newSources = _currentlySelectedDBSources
		//check if selected sources is in available source
		.filter((item) => {
			let s = availableSources.find((a) => a.name === item.source);
			return s !== undefined;
		})
		//set daterange if not specified in selected source
		.map((item) => {
			let s = availableSources.find((a) => a.name === item.source);
			let dateFilter = s.date_filter; //use date filter true/false
			let dateRange = getAktuelleLegislatur();

			if (item.startDate !== "" && item.endDate !== "") {
				dateRange = [item.startDate, item.endDate]; //use predefined date eg from examplePrompts
			}

			return {
				id: window.crypto.randomUUID(),
				type: "database",
				origin: "database",
				label: item.source.toString(),
				content: "",
				date_filter: dateFilter,
				meta: {
					date_range: dateRange,
				},
			};
		});

	sources = [
		...sources,
		..._newSources.filter(
			(item) => sources.findIndex((source) => source.label === item.label) < 0,
		),
	]; // add sources if they are not already in sources-array

	_currentlySelectedDBSources = []; // reset selection in multiselect field
	updateSendButtonTooltip();
}

function addCompleteDatabse(startDate = "", endDate = "") {
	// if whole database is already selected, then return
	if (sources.some((item) => item.type === "database_all_sources")) {
		return;
	}

	// if particular sources from db had been selected previously, then remove them
	sources = sources.filter((item) => item.type !== "database");

	let dateRange = getAktuelleLegislatur();
	if (startDate !== "" && endDate !== "") {
		dateRange = [startDate, endDate]; //use predefined date eg from examplePrompts
	}

	// add complete database as source
	sources = [
		...sources,
		{
			id: window.crypto.randomUUID(),
			type: "database_all_sources",
			origin: "database",
			label: "Datenbank",
			content: "",
			date_filter: true,
			meta: {
				date_range: dateRange,
			},
		},
	];
	updateSendButtonTooltip();
}

function removeSource(dbSource) {
	/*
        Remove item from selected sources
         */
	sources = sources.filter((item) => item !== dbSource);
	const file = document.getElementById("myFile");
	file.value = "";
	updateSendButtonTooltip();
}

function updateRequestState(
	timestamp,
	type,
	state,
	question,
	request_answers,
	model,
	filters = [],
) {
	/*
        update answers or create new request
        */
	let request_state = {
		type: type,
		state: state,
		timestamp: timestamp,
		question: question,
		answers: request_answers,
		model: model,
		filters: filters,
	};

	let _answers = [];

	let current_request = answers.find(
		(item) => item.timestamp === timestamp && item.type === type,
	);

	if (current_request) {
		// if request already exists
		current_request = {
			...current_request,
			state: state,
			answers: request_answers,
		};
		_answers = [
			...answers.filter(
				(item) => !(item.timestamp === timestamp && item.type === type),
			),
			current_request,
		];
	} else {
		// if this request does not exists in answers
		_answers = [...answers, request_state];
	}

	// sort answers
	_answers = _answers
		.sort((a, b) => {
			if (a.timestamp > b.timestamp) {
				return -1;
			}
			if (a.timestamp === b.timestamp) {
				if (a.type < b.type) {
					return -1;
				}
				if (a.type > b.type) {
					return 1;
				}
				return 0;
			}
			if (a.timestamp < b.timestamp) {
				return 1;
			}
		})
		.reverse();

	answers = _answers;
}

async function fetchRAGResponseDatabase(timestamp) {
	/*
        Send RAG request on knowledge database ressource
         */

	// filter sources to be sent to rest api via qa endpoint
	let _sources = sources.filter(
		(s) => s.type === "database" || s.type === "database_all_sources",
	);

	// if we have no sources of type "file" or "clipboard" --> leave this function
	if (_sources.length === 0) {
		return;
	}

	let format_date_range = (source) => {
		return {
			start_date: source.meta.date_range[0],
			end_date: source.meta.date_range[1],
		};
	};

	// If no filters are given (empty list or None), then the whole database is used
	// i.e. if we have only an item of type "database_all_sources", then this expression results in an empty list
	const meta_data_filters = _sources
		.filter(
			(item) =>
				item.type === "database" || item.type === "database_all_sources",
		)
		.map((s) => ({
			source: s.type === "database" ? s.label : null,
			...format_date_range(s),
		}));

	const body = {
		question: currentQuestion,
		meta_data_filters: meta_data_filters,
		language_model: llm.name,
		max_chunks_to_use: llm.max_chunks_to_use,
		request_timestamp: timestamp,
	};

	const model = { ...llm }; // model used for answer generation
	updateRequestState(
		timestamp,
		"database",
		"pending",
		currentQuestion,
		[],
		model,
		meta_data_filters,
	);

	// call API
	const url = `${process.env.API_URL}/rag/database`;
	await fetch(url, {
		method: "POST",
		body: JSON.stringify(body),
		headers: {
			"Content-Type": "application/json",
		},
	})
		.then((resp) => {
			return resp.json();
		})
		.then((resp) => {
			console.log("RESP", resp);
			return resp.map((item) => ({
				...item,
				type: "database",
				timestamp: timestamp,
				rating: 0,
			}));
		})
		.then((resp) => {
			updateRequestState(
				timestamp,
				"database",
				"done",
				currentQuestion,
				resp,
				model,
			);
		})
		.then(() => {
			currentQuestion = "";
			resetTextAreaHeight();
		})
		.catch((reason) => {
			console.debug(`${uiTexts.rag.genericError}·${reason}`);
			updateRequestState(
				timestamp,
				"database",
				"error",
				currentQuestion,
				[],
				model,
			);
		});
}

async function fetchRAGResponse(timestamp) {
	/*
        Fetch RAG results for Clipboard and File uploads
         */

	// filter sources to be sent to rest api via file-qa endpoint
	const _sources = sources.filter(
		(s) => s.type === "file" || s.type === "clipboard",
	);

	// if we have no sources of type "file" or "clipboard" --> leave this function
	if (_sources.length === 0) {
		return;
	}

	// collect the parameters for the rest api as form data
	let formData = new FormData();
	let files = [];

	// add files
	sources
		.filter((s) => s.type === "file")
		.forEach((item) => {
			formData.append("files", item.content);
			files.push({ source: item.label });
		});

	// add questions
	formData.append("question", currentQuestion);

	// add LLM for inference
	formData.append("language_model", llm.name);

	// add number of chunks to use for answer generation at max
	formData.append("max_chunks_to_use", llm.max_chunks_to_use);

	// add timestamp
	formData.append("request_timestamp", timestamp);

	const model = { ...llm }; // model used for answer generation
	updateRequestState(
		timestamp,
		"file",
		"pending",
		currentQuestion,
		[],
		model,
		files,
	);

	// call API
	const url = `${process.env.API_URL}/rag/file`;
	await fetch(url, {
		method: "POST",
		body: formData,
	})
		.then((resp) => resp.json())
		.then((resp) => {
			console.log("qa/file", resp);
			return resp.map((item) => ({
				...item,
				type: "file",
				timestamp: timestamp,
				rating: 0,
			}));
		})
		.then((resp) =>
			updateRequestState(
				timestamp,
				"file",
				"done",
				currentQuestion,
				resp,
				model,
			),
		)
		.then(() => {
			currentQuestion = "";
			resetTextAreaHeight();
		})
		.catch((reason) => {
			console.debug(`${uiTexts.rag.genericError}·${reason}`);
			updateRequestState(
				timestamp,
				"file",
				"error",
				currentQuestion,
				[],
				model,
			);
		});
}

function send_request() {
	/*
        Sends requests to REST-API
         */
	const timestamp = new Date().getTime();
	fetchRAGResponse(timestamp);
	fetchRAGResponseDatabase(timestamp);
}

function handleEnterKey(event) {
	// send message on [Enter]
	if (event.key === "Enter" && !event.shiftKey) {
		if (sources.length > 0 && currentQuestion.length > 0) {
			send_request();
		}
		event.preventDefault();
	}

	// newline on [SHIFT+Enter]
	if (event.key === "Enter" && event.shiftKey) {
		currentQuestion = `${currentQuestion}\n`;
		event.preventDefault();
	}
}

function toggleDbSource() {
	showDBSourceSelect = !showDBSourceSelect;
}

// show/hide datepicker in source-kontext
let visibleDBSourceDatapicker = "";

async function giveFeedback(selected_qa, rating, feedback_text) {
	selected_qa.rating = rating;

	const body = {
		feedback: rating,
		feedback_text: feedback_text,
		llm: {
			name: llm.name,
			label: llm.label,
			is_remote: llm.is_remote,
		},
		question: selected_qa.question,
		rag_answer: selected_qa.answer,
		source_chunks: selected_qa.sources,
		source_type: selected_qa.type,
	};

	sendFeedback(body, `${process.env.API_URL}/feedback/rag`);
}

function resetUI() {
	sources = [];
	answers = [];
	currentQuestion = "";
	const file = document.getElementById("myFile");
	file.value = "";
	resetTextAreaHeight();
}

function resetTextAreaHeight() {
	resetTextArea();
}

function getSources(request_item) {
	/*
        Show details of used sources (name and daterange)
        */
	let returnString = `${uiTexts.rag.knowledgeSourcePrefix}·${uiTexts.rag.fullDatabase}`;

	if (request_item.type !== "database") {
		request_item.filters.forEach((filter, idx) => {
			returnString += filter.source;
			if (request_item.filters.length - 1 !== idx) {
				returnString += ", ";
			}
		});
	} else {
		if (request_item.filters[0].source == null) {
			returnString += ` ${getDateRangeString(request_item.filters[0])}`;
		} else {
			request_item.filters.forEach((filter, idx) => {
				let s = availableSources.find((a) => a.name === filter.source);
				if (s !== undefined) {
					returnString += filter.source;
					if (s.date_filter) {
						returnString += ` ${getDateRangeString(filter)}`;
					}
					if (request_item.filters.length - 1 !== idx) {
						returnString += ", ";
					}
				}
			});
		}
	}

	return returnString;
}

function getPDFLink(source) {
	/*Extract url to pdf from meta data and check for page reference*/

	let pdfUrl = source.url;
	if (
		source.meta["Seite im Dokument"] !== undefined &&
		source.meta["Seite im Dokument"] != null
	) {
		pdfUrl += `#page=${source.meta["Seite im Dokument"]}`;
	}
	return pdfUrl;
}

$: if (currentQuestion != null) {
	updateSendButtonTooltip();
}

function updateSendButtonTooltip() {
	if (sources.length === 0) {
		sendButtonTooltip = uiTexts.rag.tooltipSelectSource;
	} else if (currentQuestion === "") {
		sendButtonTooltip = uiTexts.rag.tooltipEnterRequest;
	} else {
		sendButtonTooltip = uiTexts.rag.tooltipSend;
	}
}

function useExamplePrompt(prompt) {
	/*Insert example prompt. If specified, set sources, dateranges and LLM.*/
	sources = [];

	if (!onlyDocuments) {
		if (prompt.source_1 === "Gesamte Datenbank") {
			if (
				prompt.dateFilter_1 !== "" &&
				prompt.dateFilter_1 !== "Aktuelle Legislaturperiode"
			) {
				let startDate = formatDate(prompt.dateFilter_1.split("-")[0]);
				let endDate = formatDate(prompt.dateFilter_1.split("-")[1]);
				addCompleteDatabse(startDate, endDate);
			} else {
				addCompleteDatabse();
			}
		} else {
			_currentlySelectedDBSources = [];
			addSourcefromExamplePrompt(prompt, "source_1", "dateFilter_1");
			addSourcefromExamplePrompt(prompt, "source_2", "dateFilter_2");
			addSourcefromExamplePrompt(prompt, "source_3", "dateFilter_3");
			addDatabaseSources();
		}
	}

	currentQuestion = prompt.samplePrompt;
}

function addSourcefromExamplePrompt(prompt, source, date) {
	if (prompt[source] !== "") {
		let newSource = {
			source: prompt[source],
			startDate: "",
			endDate: "",
		};

		if (prompt[date] !== "Aktuelle Legislaturperiode" && prompt[date] !== "") {
			newSource.startDate = formatDate(prompt[date].split("-")[0]);
			newSource.endDate = formatDate(prompt[date].split("-")[1]);
		}

		_currentlySelectedDBSources.push(newSource);
	}
}
</script>

<svelte:head>
  {#if !onlyDocuments}
    <title>{uiTexts.pageTitles.research}</title>
  {:else}
    <title>{uiTexts.pageTitles.askTheText}</title>
  {/if}
</svelte:head>

<main style="padding-top: 90px;height: calc(100% - 120px);">
  {#if !status}
    {#if !onlyDocuments}
      <Warning>{rechercheNotAvailableText}</Warning>
    {:else}
      <Warning>{askTheDocNotAvailableText}</Warning>
    {/if}
  {:else}
    {#if !llm || llm.is_remote}
      <Warning>{warningCloud}</Warning>
    {/if}

    <div
      in:fly={{ y: -20, duration: 500, delay: 200 }}
      class="page-container"
      id="scrollTo"
    >
      <div class="hinweis">
        <div style="flex:1;padding-right:24px">
          {#if !onlyDocuments}
            <p style="padding-top:0" class="textlink">
              {@html rechercheText}
            </p>
          {:else}
            <p style="padding-top:0" class="textlink">
              {@html askTheDocText}
            </p>{/if}
        </div>
        <div class="reset">
          <button class="secondary delete" on:click={resetUI}
            >{uiTexts.rag.resetButton}
          </button>
        </div>
      </div>

      <!-- Eingabe Box-->
      <div class="input-box-with-sources">
        <!-- Input Optionen: File Upload, aus Zwischenablage, Wissensdatenbankd-->
        <div class="input-options">
          <!-- Wissensdatenbank-->

          {#if !onlyDocuments}
            <div class="database" style=" flex:1">
              <button
                aria-label={uiTexts.rag.ariaSelectAllDb}
                class="btn secondary"
                on:click={addCompleteDatabse}
                style="flex:1;text-align:left;justify-content:left"
                disabled={availableSources.length == 0}
                title={availableSources.length == 0
                  ? uiTexts.rag.tooltipDbNotAvailable
                  : uiTexts.rag.tooltipSelectAllDb}
                >{uiTexts.rag.btnSelectDb}
              </button>

              <button
                aria-label={uiTexts.rag.ariaSelectDbSource}
                class="btn secondary db-select"
                on:click={toggleDbSource}
                class:select-open={showDBSourceSelect}
                style="border-left: 0;"
                title={availableSources.length == 0
                  ? uiTexts.rag.tooltipDbNotAvailable
                  : uiTexts.rag.tooltipSelectDbSource}
                disabled={availableSources.length == 0}
                ><i class="chevron"></i>
              </button>

              {#if showDBSourceSelect}
                <div class="db-source">
                  <div>
                    <label for="quelle">{@html uiTexts.rag.multiSelectLabel}</label>
					<br />
                    <select
                      id="quelle"
                      style="overflow:auto;width:100%"
                      name="quelle"
                      multiple
                      bind:value={_currentlySelectedDBSources}
                      on:dblclick={() =>
                        addDatabaseSources() || toggleDbSource()}
                    >
                      {#each availableSources as availableSource}
                        <option value={availableSource.name}
                          >{availableSource.name}</option
                        >
                      {/each}
                    </select>
                    <button
                      style="margin: auto;"
                      on:click={() => addDatabaseSources() || toggleDbSource()}
                    >
                      {uiTexts.rag.btnApply}
                    </button>
                  </div>
                </div>
              {/if}
            </div>
          {/if}
          <!-- File Upload-->
          <FileUploadButton id="myFile" addFiles={(files) => addFiles(files)} />
          <!-- Aus Zwischenablage-->
          <div class="zwischenablage-input" style=" flex:1">
            <button
              class="secondary"
              on:click={addTextFromClipboard}
              style="width:100%;text-align:left;justify-content:left"
            >
              {uiTexts.rag.btnPasteFromClipboard}
            </button>
          </div>
          {#if onlyDocuments}
            <div style=" flex:1"></div>
          {/if}
        </div>
        <!-- Ende Input Optionen-->

        <!-- Ausgewähle Quellen-->
        <div class="source-overview">
          {#each sources as source}
            {#if source.type === "clipboard"}
              <div class="source-item">
                <div class="label">{source.label}</div>
                <button
                  aria-label={uiTexts.rag.ariaRemoveSource}
                  title={uiTexts.rag.tooltipRemoveSource}
                  class="delete-source source-button"
                  on:click={() => {
                    removeSource(source);
                  }}
                ></button>
              </div>
            {/if}

            {#if source.type === "file"}
              <div class="source-item">
                <div class="label">{source.label}</div>
                <button
                  aria-label={uiTexts.rag.ariaRemoveSource}
                  title={uiTexts.rag.tooltipRemoveSource}
                  class="delete-source source-button"
                  on:click={() => removeSource(source)}
                ></button>
              </div>
            {/if}

            {#if source.type === "database" || source.type === "database_all_sources"}
              <div class="source-item" id="source-item-{source.id}">
                {#if source.type === "database_all_sources"}
                  <div class="label">{uiTexts.rag.sourceLabelAllDb}</div>
                {:else}
                  <div class="label">{source.label}</div>
                {/if}

                {#if source.date_filter}
                  <button
                    aria-label={uiTexts.rag.ariaSelectTime}
                    title={uiTexts.rag.tooltipSelectTime}
                    class="show-calendar"
                    on:click={() =>
                      visibleDBSourceDatapicker === ""
                        ? (visibleDBSourceDatapicker = source.id)
                        : (visibleDBSourceDatapicker = "")}
                  ></button>
                  <DateFilter
                    bind:source
                    bind:sources
                    bind:visibleDBSourceDatapicker
                  />
                {/if}

                <button
                  aria-label={uiTexts.rag.ariaRemoveSource}
                  title={uiTexts.rag.tooltipRemoveSource}
                  class="delete-source"
                  on:click={() => removeSource(source)}
                ></button>
              </div>
            {/if}
          {/each}

          <div class="add-new-source"></div>
        </div>
        <!-- Ende Ausgewähle Quellen-->

        <InputTextarea
          bind:newMessage={currentQuestion}
          {handleEnterKey}
          bind:reset={resetTextArea}
          bind:isTextFieldActive
          name="rag-eingabe"
          placeholder={uiTexts.rag.inputPlaceholder}
        ></InputTextarea>

        <div class="input-actions">
          <div class="halluzination-warning">
            {@html warningRecherche}
          </div>
          <div
            style="display:flex;flex-direction:row; align-items:end; gap:20px;"
          >
            <ModelSelection
              bind:relevantStore={uiStore.active_llms_recherche}
              bind:availableLLMs={available_llms}
              bind:llmIsSelected={llm_selected_by_user}
              bind:selectedLLM={llm}
              bind:this={modelSelectionRef}
            />
            <button
              class="send-button"
              aria-label={uiTexts.rag.sendAriaLabel}
              disabled={sources.length === 0 || currentQuestion.length === 0}
              on:click={() => send_request()}
              title={sendButtonTooltip}
            >
            </button>
          </div>
        </div>
      </div>

      <!-- /Eingabe Box -->

      <!-- Antworten -->
      <div class="chat-history">
        {#each Object.entries(Object.groupBy(answers, ({ timestamp }) => timestamp)) as [timestamp, requests]}
          {#if requests.length > 0}
            <div class="answer-group">
              <div class="frage">
                <span class="flex1">
                  {requests[0].question}
                </span>

                <span style="font-size: small; color: #999999">
                  {requests[0].model.label}
                </span>

                <button
                  title={uiTexts.rag.tooltipDeleteQuestion}
                  class="delete-question source-button"
                  aria-label={uiTexts.rag.ariaDeleteQuestion}
                  on:click={() =>
                    (answers = answers.filter(
                      (item) => parseInt(item.timestamp) !== parseInt(timestamp)
                    ))}
                ></button>
              </div>

              <div class="answers">
                {#each requests as request_item}
                  <div class="rag-item">
                    <span class="answer-source-type">
                      {getSources(request_item)}
                    </span>

                    {#if request_item.state === "error"}
                      <p class="common-paragraph" role="status">
                        {uiTexts.rag.requestError}
                      </p>
                    {/if}

                    {#if request_item.state === "pending"}
                      <SkeletonLoader />
                    {/if}

                    {#if request_item.state === "done"}
                      <div class="antwort-box">
                        {#each request_item.answers as answer}
                          <div class="antwort">
                            {@html
                              renderMarkdown(answer.answer)}
                            <div class="answer-actions">
                              <FeedbackButtons
                                messageContent={answer.answer}
                                buttonClicked={answer.rating}
                                callback={(rating, feedback_text) =>
                                  giveFeedback(answer, rating, feedback_text)}
                              />
                            </div>

                            {#if answer.sources && answer.sources.length > 0}
                              <div class="answer-sources">
                                <div style="margin-bottom:25px;">
                                  <span class="heading"
                                    >{uiTexts.rag.answerSourcesHeader}</span
                                  >
                                </div>

                                {#each answer.sources as source}
                                  <!-- es können mehrere quellen geben eine Accordion Logik ist hier angedacht  -->
                                  <div class="source-data-box">
                                    <div>
                                      {#if source.meta && source.meta["Titel"] && source.url}
                                        {uiTexts.rag.sourcePrefix}
                                        <a
                                          href={getPDFLink(source)}
                                          class="textlink icon-ext-link"
                                          target="_blank"
                                          >{source.meta["Titel"]}</a
                                        >
                                      {:else if source.meta && source.meta["Titel"]}
                                        {uiTexts.rag.sourcePrefix}
                                        <span class="source-title">{source.meta["Titel"]}</span>
                                      {:else if source.meta && source.meta["Datei"]}
                                        {uiTexts.rag.sourcePrefix}
                                        <span class="source-title">{source.meta["Datei"]}</span>
                                      {/if}
                                      {#if source.meta && source.meta["Datum"]}
                                        ({source.meta["Datum"]})
                                      {/if}
                                    </div>
                                    <div class="source-text">
                                      <Accordeon {source} />
                                    </div>
                                  </div>
                                  <!-- ENDE Quelle -->
                                {/each}
                              </div>
                            {/if}
                          </div>
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/if}
        {/each}
      </div>

      <!-- /Example Prompts -->
      {#if examplePrompts != null && examplePrompts.length > 0}
        <ExamplePrompts
          prompts={examplePrompts}
          translationKey="examplePromptsRecherche"
          callback={(prompt) => useExamplePrompt(prompt)}
        />
      {/if}
    </div>

    <DragAndDrop {isDropzoneActive} addFiles={(files) => addFiles(files)} />
  {/if}
</main>

<style>
  a {
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }

  .source-title{
	font-weight: 600;
  }

  input {
    margin: 0px;
  }
  * {
    box-sizing: border-box;
  }

  .flex1 {
    flex: 1;
    margin-right: 8px;
  }

  .hinweis p {
    font-size: 1rem;
  }

  .input-options {
    display: flex;
    gap: 10px;
  }

  .input-options .database {
    position: relative;
    display: flex;
  }

  .input-box-with-sources {
    background-color: var(--bg-color);
    padding: 10px;
  }

  .source-overview {
    display: flex;
    padding-top: 12px;
    flex-flow: row wrap;
  }

  .label {
    max-width: 130px;
  }

  .source-data-box {
    padding-bottom: 12px;
    border-bottom: 2px solid var(--bg-color);
    margin-bottom: 12px;
  }
  .source-data-box:last-child {
    border-bottom: unset;
  }

  .source-text {
    font-size: 0.85rem;
    padding-bottom: 2.5rem;
    position: relative;
  }

  .btn {
    font-size: 1rem;
    line-height: 1.2rem;
    padding: 8px;
    display: flex;
    text-transform: none;
  }

  .btn.secondary,
  button.secondary {
    background-color: #fff;
    border: 2px solid #000;
    color: #000;
    cursor: pointer;
    text-transform: none;
    font-weight: bold;
  }

  .btn.secondary:hover,
  button.secondary:hover {
    background-color: var(--bg-color);
  }

  .db-source {
    position: absolute;
    top: 40px;
    background: #fff;
    padding: 8px;
    box-shadow: 0 3px 9px rgba(0, 0, 0, 0.3);
    z-index: 1;
    width: 100%;
    text-align: left;
  }

  .db-source label {
    color: #555;
    font-size: 1rem;
  }

  .database button:first-child {
    padding-left: 40px;
    background-image: url(static/icon-datenbank.svg);
    background-repeat: no-repeat;
    background-position: left center;
    background-size: 40px;
  }

  .chevron {
    width: 16px;
    height: 16px;
    background-image: url('data:image/svg+xml;utf8,<svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><polyline points="4 6 8 10 12 6" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    display: inline-block;
  }
  .database button.db-select.select-open .chevron {
    transform: rotate(180deg);
  }

  button.show-calendar,
  button.delete-question,
  button.delete-source {
    background-color: transparent;
    border: none;
  }

  .show-calendar {
    width: 14px;
    height: 14px;
    background-image: url('data:image/svg+xml;utf8,<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><rect x="1.5" y="3" width="11" height="9" fill="none" stroke="black" stroke-width="2"/><line x1="1.5" y1="5" x2="12.5" y2="5" stroke="black" stroke-width="2"/><line x1="3.5" y1="1.5" x2="3.5" y2="3" stroke="black" stroke-width="2"/><line x1="10.5" y1="1.5" x2="10.5" y2="3" stroke="black" stroke-width="2"/></svg>');
    background-repeat: no-repeat;
    background-size: contain;
    margin-left: 8px;
  }

  .chat-history {
    width: 100%;
    text-align: left;
    padding: 10px 0;
    display: flex;
    flex-direction: column-reverse;
  }

  .chat-history > div {
    display: flex;
  }

  .chat-history > div {
    display: flex;
    margin-bottom: 30px;
    align-items: flex-start;
  }

  .rag-item {
    flex-direction: column;
    width: 100%;
  }

  .answer-group .frage {
    display: flex;
    background-color: var(--bg-color);
    padding: 6px 12px;
    font-weight: bold;
    width: 100%;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    margin: 10px 0;
    font-size: large;
  }

  .rag-item .antwort-box {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .antwort {
    padding-top: 12px;
    padding-bottom: 12px;
    border-bottom: 2px solid var(--bg-color);
    margin-bottom: 12px;
  }

  .antwort-box .antwort:last-child {
    margin-bottom: 0px;
  }

  .answer-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 50px;
  }

  .answer-sources {
    margin: 25px 25px 25px 25px;
  }

  .answer-sources .heading {
    color: #666;
    font-size: large;
    margin-bottom: 20px;
  }

  .common-paragraph {
    font-size: 1rem;
    margin: 0 0 10px;
  }

  @keyframes typing {
    0%,
    40%,
    100% {
      transform: initial;
      background-color: #999;
    }
    20% {
      transform: scale(1.3); /* vertikal animieren translatey(-1rem);*/
      background-color: var(--sec-color); /*gelb: ff0*/
    }
  }

  @media screen and (max-width: 995px) {
    .zwischenablage-input:after {
      display: none;
    }
  }

  @media screen and (max-width: 600px) {
    .input-options {
      display: block;
    }
    .input-options > div {
      margin-bottom: 8px;
    }
  }
</style>
